/**
 * API Service for AWS Training Assistant
 * Handles communication with the backend API
 * 
 * This service provides NON-STREAMING API calls.
 * For streaming responses, use streamingApi.ts instead.
 * 
 * Endpoint: POST /api/ask
 * - Returns complete response after AI generation finishes
 * - Subject to 29-second API Gateway timeout
 * - Suitable for short responses
 * 
 * For streaming endpoint (POST /api/stream/chat), see streamingApi.ts
 */

import axios, { AxiosError, AxiosInstance } from 'axios';
import { QuestionRequest, AssistantResponse, ErrorResponse } from '../types';
import { fetchAuthSession } from 'aws-amplify/auth';

// Get API endpoint from environment variable
// Use /api path when in production (behind CloudFront with /api/* behavior)
// Use absolute URL for local development
const API_ENDPOINT = process.env.NODE_ENV === 'production' 
  ? '/api' // CloudFront routes /api/* to API Gateway /prod/api/*
  : process.env.REACT_APP_API_ENDPOINT || 'http://localhost:3001';

/**
 * Create and configure axios instance with authentication
 */
async function createApiClient(): Promise<AxiosInstance> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  try {
    // Get the current auth session
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (token) {
      // API Gateway Cognito authorizer expects "Bearer <token>" format
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (error) {
    console.warn('No authentication token available');
  }

  return axios.create({
    baseURL: API_ENDPOINT,
    timeout: 30000, // 30 seconds to match API Gateway timeout
    headers,
  });
}

/**
 * Submit a question to the AWS Training Assistant API
 * 
 * @param question - The user's question about AWS services
 * @returns Promise resolving to the assistant's response
 * @throws Error with user-friendly message for various failure scenarios
 */
export async function submitQuestion(question: string): Promise<AssistantResponse> {
  const apiClient = await createApiClient();
  
  try {
    const requestData: QuestionRequest = { question };
    
    const response = await apiClient.post<AssistantResponse>('/ask', requestData);
    
    return response.data;
  } catch (error) {
    // Handle axios-specific errors
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      
      // Network error (no response received)
      if (!axiosError.response) {
        if (axiosError.code === 'ECONNABORTED') {
          throw new Error('The request took too long. Please try again.');
        }
        throw new Error('Unable to connect to the server. Please check your internet connection.');
      }
      
      // Server returned an error response
      const errorResponse = axiosError.response.data;
      if (errorResponse && errorResponse.message) {
        throw new Error(errorResponse.message);
      }
      
      // Fallback error message based on status code
      if (axiosError.response.status >= 500) {
        throw new Error('A server error occurred. Please try again later.');
      } else if (axiosError.response.status >= 400) {
        throw new Error('Invalid request. Please check your input and try again.');
      }
    }
    
    // Generic error fallback
    throw new Error('An error occurred. Please try again.');
  }
}
